import invert from 'lodash/invert';
import sortBy from 'lodash/sortBy';

import { mediaTypeColors, platformColors, sentimentColors } from '@/config';
import { CHART, colours } from '@/ux/colours';

export const PREVIEW_TIME_RANGE_IN_DAYS = 28; // last 4 weeks
export const MIN_SCALE_WIDTH = 35;
export const VISUAL_TRENDS_PREVIEW_MAX_POSTS = 5;
export const VISUAL_FILTER_SEARCH_MAX_MEDIA = 5;
export const OVERVIEW_TOP_POSTS_TO_FETCH = 8;
export const TRENDS_NUM_POSTS_TO_FETCH = 5;
export const WEB_RESULT_PREVIEW_MAX_POSTS = 4;
export const WEB_RESULTS_LIMIT = 24;
export const WEB_RESULT_PAGE_INITIAL_MAX_POSTS = 50;
export const WEB_POSTS_FETCH_LIMIT = 25;
export const WEB_POSTS_MAX_POSTS_TOTAL = 1000;
export const TOP_POSTS_INITIAL_MAX_POSTS = 50;
export const TOP_POSTS_FETCH_LIMIT = 25;
export const TOP_POSTS_MAX_POSTS_TOTAL = 1000;
export const VISUAL_TREND_FETCH_LIMIT = 25;
export const VISUAL_TREND_MAX_POSTS_TOTAL = 1000;
export const SENTIMENT_LABEL = 'Sentiment - Caption';
export const metricFormats = {
  NUMBER: 'number',
  PERCENT: 'percent',
  STRING: 'string',
};
export const TOPIC_MAX_KEYWORD_COUNT = 100;

export const TRENDING_KEYWORDS_COUNT = {
  COLLAPSED: 10,
  EXPANDED: 25,
};

export const TOP_LOCATIONS_COUNT = {
  COLLAPSED: 10,
  EXPANDED: 25,
};

export const SHARE_OF_VOICE_COMPETITORS_MAX_COUNT = 12;

export const HISTORICAL_IMPORT_MINIMUM_DATE = new Date(2022, 3, 1);

export const SOCIAL_LISTENING_ROUTE_NAMES = {
  OVERVIEW: 'listening.overview',
  QUICK_SEARCH: 'listening.quick-search',
  TRENDS: 'listening.trends',
  TRENDS_REPORT: 'listening.trends.report',
  NO_ORG_ACCESS: 'listening.upgrade',
  NO_USER_ACCESS: 'listening.no-access',
  TOPICS: 'listening.topics',
  TOPICS_REPORT: 'listening.topics.report',
  NO_TOPIC: 'listening.no-topic',
  TOPICS_TOP_POSTS: 'listening.topics.top-posts',
  TOP_POSTS: 'listening.top-posts',
  QUICK_SEARCH_TOP_POSTS: 'listening.quick-search.top-posts',
  VISUAL_TRENDS: 'listening.visual-trends',
  MANAGE_USAGE: 'listening.manage-usage',
  PUBLIC_MEDIA: 'listening.public-media',
  WEB_RESULTS: 'listening.web-results',
  TOPIC_MONITOR: 'listening.topics.monitor',
  NO_TOPIC_COMPARISONS: 'listening.no-topic-comparisons',
  TOPIC_COMPARISONS: 'listening.topic-comparisons',
};

export const SOCIAL_LISTENING_ROUTES = {
  ROOT: {
    name: 'socialListening',
    path: '',
    label: 'Social Listening',
  },
  OVERVIEW: {
    name: SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW,
    path: 'overview',
    label: 'Overview',
  },
  TRENDS: {
    name: SOCIAL_LISTENING_ROUTE_NAMES.TRENDS,
    path: 'trends',
    label: 'Trends',
  },
  TOPICS: {
    name: SOCIAL_LISTENING_ROUTE_NAMES.TOPICS,
    path: 'topics/:id',
    label: 'Topics',
  },
  TOPIC_COMPARISONS: {
    name: SOCIAL_LISTENING_ROUTE_NAMES.TOPIC_COMPARISONS,
    path: 'comparisons/:id',
    label: 'Comparisons',
  },
};

export const SOCIAL_LISTENING_BREADCRUMBS = {
  OVERVIEW: [SOCIAL_LISTENING_ROUTES.ROOT, SOCIAL_LISTENING_ROUTES.OVERVIEW],
  TRENDS: [SOCIAL_LISTENING_ROUTES.ROOT, SOCIAL_LISTENING_ROUTES.TRENDS],
  TOPICS: [SOCIAL_LISTENING_ROUTES.ROOT, SOCIAL_LISTENING_ROUTES.TOPICS],
};

export const FILTER_TYPE = {
  CHANNELS: { value: 'channels', text: 'Channel' },
  SENTIMENTS: { value: 'sentiments', text: 'Sentiment' },
  MEDIA_TYPES: { value: 'mediaTypes', text: 'Media Type' },
  KEYWORD: { value: 'keyword', text: 'Keyword' },
  MENTION_TYPES: { value: 'mentionTypes', text: 'Mention Type' },
  DATE_RANGE: { value: 'dateRange', text: 'Date Range' },
  SORT_BY: { value: 'sortBy', text: 'Sort By' },
  VISUAL: { value: 'visual', text: 'Visual' },
  KEYWORD_TYPE: { value: 'keywordTypes', text: 'Keyword Type' },
};

export const SOCIAL_LISTENING_CHANNEL_ICON_NAMES = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  LINKEDIN: 'linkedin',
  REDDIT: 'reddit',
  TIKTOK: 'tiktok',
  TWITTER: 'twitter',
  WEB: 'web',
  YOUTUBE: 'youtube',
};

export const SENTIMENT_COLORS = {
  POSITIVE: sentimentColors.positive,
  NEGATIVE: sentimentColors.negative,
  NEUTRAL: sentimentColors.neutral,
};

export const SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED = {
  INSTAGRAM: {
    title: 'Instagram',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.INSTAGRAM,
    value: 'INSTAGRAM',
    chartDataKey: 'INSTAGRAM',
  },
  LINKEDIN: {
    title: 'LinkedIn',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.LINKEDIN,
    value: 'LINKEDIN',
    chartDataKey: 'LINKEDIN',
  },
  REDDIT: {
    title: 'Reddit',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.REDDIT,
    value: 'REDDIT',
    chartDataKey: 'REDDIT',
  },
  TIKTOK: {
    title: 'TikTok',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.TIKTOK,
    value: 'TIKTOK',
    chartDataKey: 'TIKTOK',
  },
  WEB: {
    title: 'Web',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.WEB,
    value: 'WEB',
    chartDataKey: 'WEB',
  },
  TWITTER: {
    title: 'X',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.TWITTER,
    value: 'TWITTER',
    chartDataKey: 'X',
  },
  YOUTUBE: {
    title: 'YouTube',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.YOUTUBE,
    value: 'YOUTUBE',
    chartDataKey: 'YOUTUBE',
  },
};

export const SOCIAL_LISTENING_CHANNEL_OPTIONS = {
  FACEBOOK: {
    title: 'Facebook',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.FACEBOOK,
    value: 'FACEBOOK',
    chartDataKey: 'FACEBOOK',
  },
  INSTAGRAM: {
    title: 'Instagram',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.INSTAGRAM,
    value: 'INSTAGRAM',
    chartDataKey: 'INSTAGRAM',
  },
  LINKEDIN: {
    title: 'LinkedIn',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.LINKEDIN,
    value: 'LINKEDIN',
    chartDataKey: 'LINKEDIN',
  },
  REDDIT: {
    title: 'Reddit',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.REDDIT,
    value: 'REDDIT',
    chartDataKey: 'REDDIT',
  },
  TIKTOK: {
    title: 'TikTok',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.TIKTOK,
    value: 'TIKTOK',
    chartDataKey: 'TIKTOK',
  },
  WEB: {
    title: 'Web',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.WEB,
    value: 'WEB',
    chartDataKey: 'WEB',
  },
  TWITTER: {
    title: 'X',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.TWITTER,
    value: 'TWITTER',
    chartDataKey: 'X',
  },
  YOUTUBE: {
    title: 'YouTube',
    iconName: SOCIAL_LISTENING_CHANNEL_ICON_NAMES.YOUTUBE,
    value: 'YOUTUBE',
    chartDataKey: 'YOUTUBE',
  },
};

export const SOCIAL_LISTENING_MENTION_TYPE_OPTIONS = Object.freeze({
  AUDIO: {
    title: 'Audio',
    value: 'AUDIO',
  },
  BLOG: {
    title: 'Blog',
    value: 'BLOG',
  },
  CAPTION: {
    title: 'Caption',
    value: 'CAPTION',
  },
  FORUM: {
    title: 'Forum',
    value: 'FORUM',
  },
  NEWS: {
    title: 'News',
    value: 'NEWS',
  },
});

export const SOCIAL_LISTENING_SENTIMENTS_OPTIONS = Object.freeze({
  POSITIVE: {
    value: 'POSITIVE',
    title: 'Positive',
    iconName: 'dot',
    iconColor: SENTIMENT_COLORS.POSITIVE,
    field: 'includePositive',
  },
  NEUTRAL: {
    value: 'NEUTRAL',
    title: 'Neutral',
    iconName: 'dot',
    iconColor: SENTIMENT_COLORS.NEUTRAL,
    field: 'includeNeutral',
  },
  NEGATIVE: {
    value: 'NEGATIVE',
    title: 'Negative',
    iconName: 'dot',
    iconColor: SENTIMENT_COLORS.NEGATIVE,
    field: 'includeNegative',
  },
});

export const SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS = Object.freeze({
  NET:
    'The net sentiment of post captions within this Topic during the reporting period. Net sentiment shows the overall balance between positive and negative conversation.\n' +
    '\n' +
    'Net Sentiment = (Positive Captions - Negative Captions) / Total Captions *100',
  POSITIVE: 'The percentage of posts within this Topic with a positive caption sentiment',
  NEUTRAL: 'The percentage of posts within this Topic with a neutral caption sentiment',
  NEGATIVE: 'The percentage of posts within this Topic with a negative caption sentiment',
  NET_SENTIMENT: {
    content:
      'The net sentiment of post captions for each Topic in this Comparison during the reporting period. Net sentiment shows the overall balance between positive and negative conversation.',
    footer: 'Net Sentiment = (Positive Captions - Negative Captions) / Total Captions *100',
  },
});
export const SOCIAL_LISTENING_SENTIMENT_SUMMARY_CHIPS = Object.freeze({
  POSITIVE: {
    severity: 'neutral',
    badge: { severity: 'positive' },
    label: 'Positive',
  },
  NEUTRAL: {
    severity: 'neutral',
    badge: { severity: 'neutral' },
    label: 'Neutral',
  },
  NEGATIVE: {
    severity: 'neutral',
    badge: { severity: 'negative' },
    label: 'Negative',
  },
});

export const SENTIMENT_OPTIONS = new Set(
  Object.values(SOCIAL_LISTENING_SENTIMENTS_OPTIONS).map(
    (sentimentOption) => sentimentOption.value,
  ),
);

export const SOCIAL_LISTENING_SENTIMENT_METRICS = {
  isPositive: {
    label: SENTIMENT_LABEL,
    value: 'Positive',
    iconColor: SENTIMENT_COLORS.POSITIVE,
  },
  isNeutral: {
    label: SENTIMENT_LABEL,
    value: 'Neutral',
    iconColor: SENTIMENT_COLORS.NEUTRAL,
  },
  isNegative: {
    label: SENTIMENT_LABEL,
    value: 'Negative',
    iconColor: SENTIMENT_COLORS.NEGATIVE,
  },
};

export const SOCIAL_LISTENING_TOP_POST_SENTIMENT_CHIP_PROPS = {
  isPositive: {
    label: 'Positive',
    size: 'sm',
    badge: { severity: 'positive' },
  },
  isNeutral: {
    label: 'Neutral',
    size: 'sm',
    badge: { severity: 'neutral' },
  },
  isNegative: {
    label: 'Negative',
    size: 'sm',
    badge: { severity: 'negative' },
  },
};

export const SOCIAL_LISTENING_METRIC_LABELS = {
  avgEngagements: 'Avg. Engagements',
  engagements: 'Engagements',
  fractionPositive: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.title,
  fractionNeutral: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.title,
  fractionNegative: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.title,
  isPositive: SOCIAL_LISTENING_SENTIMENT_METRICS.isPositive.label,
  isNeutral: SOCIAL_LISTENING_SENTIMENT_METRICS.isNeutral.label,
  isNegative: SOCIAL_LISTENING_SENTIMENT_METRICS.isNegative.label,
};

export const SOCIAL_LISTENING_METRIC_LABELS_GDI = {
  avgEngagements: 'Average Engagements',
  engagements: 'Engagements',
  fractionPositive: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.title,
  fractionNeutral: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.title,
  fractionNegative: SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.title,
  isPositive: SOCIAL_LISTENING_SENTIMENT_METRICS.isPositive.label,
  isNeutral: SOCIAL_LISTENING_SENTIMENT_METRICS.isNeutral.label,
  isNegative: SOCIAL_LISTENING_SENTIMENT_METRICS.isNegative.label,
};

export const SOCIAL_LISTENING_METRICS = {
  avgEngagements: {
    label: SOCIAL_LISTENING_METRIC_LABELS.avgEngagements,
    format: metricFormats.NUMBER,
  },
  engagements: {
    label: SOCIAL_LISTENING_METRIC_LABELS.engagements,
    format: metricFormats.NUMBER,
  },
  fractionPositive: {
    label: SOCIAL_LISTENING_METRIC_LABELS.fractionPositive,
    iconColor: SENTIMENT_COLORS.POSITIVE,
    format: metricFormats.PERCENT,
  },
  fractionNegative: {
    label: SOCIAL_LISTENING_METRIC_LABELS.fractionNegative,
    iconColor: SENTIMENT_COLORS.NEGATIVE,
    format: metricFormats.PERCENT,
  },
};

export const SOCIAL_LISTENING_METRICS_GDI = {
  avgEngagements: {
    label: SOCIAL_LISTENING_METRIC_LABELS_GDI.avgEngagements,
    formatType: metricFormats.NUMBER,
    formatOptions: {
      notation: 'compact',
      compactDisplay: 'short',
      minAbbrNumber: 1000,
    },
  },
  fractionPositive: {
    label: SOCIAL_LISTENING_METRIC_LABELS_GDI.fractionPositive,
    formatType: metricFormats.PERCENT,
    dot: { severity: 'positive' },
  },
  fractionNegative: {
    label: SOCIAL_LISTENING_METRIC_LABELS_GDI.fractionNegative,
    formatType: metricFormats.PERCENT,
    dot: { severity: 'negative' },
  },
  fractionNeutral: {
    label: SOCIAL_LISTENING_METRIC_LABELS_GDI.fractionNeutral,
    formatType: metricFormats.PERCENT,
    dot: { severity: 'neutral' },
  },
};

export const SOCIAL_LISTENING_FILTERS = {
  FILTER_MEDIA_TYPE: 'listening_media_type',
  FILTER_CHANNEL: 'listening_channel',
  FILTER_SENTIMENT: 'listening_sentiment',
  FILTER_VISUAL: 'listening_visual',
  FILTER_KEYWORD: 'listening_keyword',
  FILTER_KEYWORD_TYPE: 'listening_keyword_type',
  FILTER_MENTION_TYPE: 'listening_mention_type',
};

export const DATE_OPTIONS = {
  ALLTIME: 'ALL_TIME',
  LASTDAY: 'LAST_DAY',
  LAST7DAYS: 'LAST_7_DAYS',
  LAST30DAYS: 'LAST_30_DAYS',
  LAST3MONTHS: 'LAST_3_MONTHS',
  LAST6MONTHS: 'LAST_6_MONTHS',
  LAST365DAYS: 'LAST_365_DAYS',
  CUSTOM: 'CUSTOM',
};

export const GRAPH_SCALE = {
  HOUR: 'HOUR',
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  CUSTOM: 'CUSTOM',
};

// used when drilling down on post volume graph to specific hour/day/week/month on top posts page
export const CUSTOM_DATE = {
  label: 'Custom',
  value: 'CUSTOM',
};
export const SOCIAL_LISTENING_DATE_OPTIONS = Object.freeze({
  LAST_DAY: {
    label: 'Last 24 Hours',
    value: DATE_OPTIONS.LASTDAY,
    subtractUnit: 'day',
    subtractValue: 1,
    scale: GRAPH_SCALE.HOUR,
  },
  LAST_7_DAYS: {
    label: 'Last 7 Days',
    value: DATE_OPTIONS.LAST7DAYS,
    subtractUnit: 'day',
    subtractValue: 7,
    scale: GRAPH_SCALE.DAY,
  },
  LAST_30_DAYS: {
    label: 'Last 30 Days',
    value: DATE_OPTIONS.LAST30DAYS,
    subtractUnit: 'day',
    subtractValue: 30,
    scale: GRAPH_SCALE.DAY,
  },
  LAST_3_MONTHS: {
    label: 'Last 3 Months',
    value: DATE_OPTIONS.LAST3MONTHS,
    subtractUnit: 'month',
    subtractValue: 3,
    scale: GRAPH_SCALE.WEEK,
  },
  LAST_6_MONTHS: {
    label: 'Last 6 Months',
    value: DATE_OPTIONS.LAST6MONTHS,
    subtractUnit: 'month',
    subtractValue: 6,
    scale: GRAPH_SCALE.MONTH,
  },
  LAST_365_DAYS: {
    label: 'Last 365 Days',
    value: DATE_OPTIONS.LAST365DAYS,
    subtractUnit: 'day',
    subtractValue: 365,
    scale: GRAPH_SCALE.MONTH,
  },
});

export const SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS = Object.freeze({
  IMAGE: {
    value: 'IMAGE',
    title: 'Photos',
    tooltip: 'Image',
  },
  VIDEO: {
    value: 'VIDEO',
    title: 'Videos',
    tooltip: 'Video',
  },
  LINK: {
    value: 'LINK',
    title: 'Links',
    tooltip: 'Link',
  },
  TEXT: {
    value: 'TEXT',
    title: 'Text',
    tooltip: 'Text',
  },
});

export const SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS = Object.freeze({
  KEYWORD: {
    value: 'KEYWORD',
    title: 'Keywords',
    tooltip: 'Keywords',
  },
  HASHTAG: {
    value: 'HASHTAG',
    title: 'Hashtags',
    tooltip: 'Hashtags',
  },
});

export const SOCIAL_LISTENING_OPTIONS_BY_FILTER = {
  [FILTER_TYPE.CHANNELS.value]: SOCIAL_LISTENING_CHANNEL_OPTIONS,
  [FILTER_TYPE.SENTIMENTS.value]: SOCIAL_LISTENING_SENTIMENTS_OPTIONS,
  [FILTER_TYPE.MEDIA_TYPES.value]: SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS,
  [FILTER_TYPE.MENTION_TYPES.value]: SOCIAL_LISTENING_MENTION_TYPE_OPTIONS,
  [FILTER_TYPE.KEYWORD_TYPE.value]: SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS,
};

export const POST_SORT_OPTIONS = Object.freeze({
  engagements: { label: 'Engagements', value: 'engagements' },
  date: {
    label: 'Published Date',
    value: 'date',
  },
});

export const TOP_PERFORMING_POST_DATE_OPTIONS = Object.freeze({
  [DATE_OPTIONS.LASTDAY]: {
    label: 'Last Day',
    value: DATE_OPTIONS.LASTDAY,
    subtractUnit: 'day',
    subtractValue: 1,
  },
  [DATE_OPTIONS.LAST7DAYS]: {
    label: 'Last 7 Days',
    value: DATE_OPTIONS.LAST7DAYS,
    subtractUnit: 'day',
    subtractValue: 7,
  },
  [DATE_OPTIONS.LAST30DAYS]: {
    label: 'Last 30 Days',
    value: DATE_OPTIONS.LAST30DAYS,
    subtractUnit: 'day',
    subtractValue: 30,
  },
  [DATE_OPTIONS.LAST3MONTHS]: {
    label: 'Last 3 Months',
    value: DATE_OPTIONS.LAST3MONTHS,
    subtractUnit: 'month',
    subtractValue: 3,
  },
  [DATE_OPTIONS.LAST6MONTHS]: {
    label: 'Last 6 Months',
    value: DATE_OPTIONS.LAST6MONTHS,
    subtractUnit: 'month',
    subtractValue: 6,
  },
  [DATE_OPTIONS.LAST365DAYS]: {
    label: 'Last 365 Days',
    value: DATE_OPTIONS.LAST365DAYS,
    subtractUnit: 'day',
    subtractValue: 365,
  },
});

export const formatChannelFilterOptions = (options) =>
  options.map((channel) => ({
    label: channel.title,
    formattedLabel: channel.title,
    icon: {
      channelIcon: true,
      name: channel.iconName,
    },
    value: channel.value,
  }));

export const SOCIAL_LISTENING_FILTER_WIDGETS = {
  [SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.CHANNELS.text,
    type: FILTER_TYPE.CHANNELS.value,
    noDuplicate: true,
    options: {
      filterOptions: formatChannelFilterOptions(Object.values(SOCIAL_LISTENING_CHANNEL_OPTIONS)),
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.SENTIMENTS.text,
    type: FILTER_TYPE.SENTIMENTS.value,
    noDuplicate: true,
    disableDelete: false,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_SENTIMENTS_OPTIONS).map((sentiment) => ({
        label: sentiment.title,
        icon: {
          name: sentiment.iconName,
          color: sentiment.iconColor,
        },
        value: sentiment.value,
      })),
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.MEDIA_TYPES.text,
    type: FILTER_TYPE.MEDIA_TYPES.value,
    icon: 'media',
    noDuplicate: true,
    disableDelete: false,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS).map((mediaType) => ({
        label: mediaType.title,
        value: mediaType.value,
      })),
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MENTION_TYPE]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.MENTION_TYPES.text,
    type: FILTER_TYPE.MENTION_TYPES.value,
    icon: 'mention',
    noDuplicate: true,
    disableDelete: false,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_MENTION_TYPE_OPTIONS).map((mentionType) => ({
        label: mentionType.title,
        value: mentionType.value,
      })),
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_KEYWORD]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.KEYWORD.text,
    noDuplicate: true,
    disableDelete: false,
    type: FILTER_TYPE.KEYWORD.value,
    options: {},
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_KEYWORD_TYPE]: {
    name: 'MediaFilterCheckboxWidget',
    label: FILTER_TYPE.KEYWORD_TYPE.text,
    icon: 'key',
    noDuplicate: true,
    disableDelete: false,
    type: FILTER_TYPE.KEYWORD_TYPE.value,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS).map((keywordType) => ({
        label: keywordType.title,
        value: keywordType.value,
      })),
    },
  },
};

export const SOCIAL_LISTENING_DEFAULT_FILTER = {
  [SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL]: {
    label: '',
    field: SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL,
    imgFilter: true,
    valueToIconMap: SOCIAL_LISTENING_CHANNEL_ICON_NAMES,
    emptyStates: false,
    type: FILTER_TYPE.CHANNELS.value,
    value: [],
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT]: {
    label: '',
    operator: 'is',
    field: SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT,
    emptyStates: false,
    type: FILTER_TYPE.SENTIMENTS.value,
    value: [],
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE]: {
    label: '',
    operator: 'is',
    field: SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE,
    emptyStates: false,
    type: FILTER_TYPE.MEDIA_TYPES.value,
    value: [],
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MENTION_TYPE]: {
    label: '',
    operator: 'is',
    field: SOCIAL_LISTENING_FILTERS.FILTER_MENTION_TYPE,
    emptyStates: false,
    type: FILTER_TYPE.MENTION_TYPES.value,
    value: [],
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_KEYWORD_TYPE]: {
    label: '',
    operator: 'is',
    field: SOCIAL_LISTENING_FILTERS.FILTER_KEYWORD_TYPE,
    emptyStates: false,
    type: FILTER_TYPE.KEYWORD_TYPE.value,
    value: [],
  },
};

export const SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT_DEPRECATED = Object.keys(
  SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED,
);

export const SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT = Object.keys(
  SOCIAL_LISTENING_CHANNEL_OPTIONS,
);

export const SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT = Object.values(
  SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS,
).map((type) => type.value);

export const SOCIAL_LISTENING_MENTIONTYPE_FILTER_DEFAULT = Object.values(
  SOCIAL_LISTENING_MENTION_TYPE_OPTIONS,
).map((type) => type.value);

export const SOCIAL_LISTENING_KEYWORDTYPE_FILTER_DEFAULT = Object.values(
  SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS,
).map((type) => type.value);

export const SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT = Object.values(
  SOCIAL_LISTENING_SENTIMENTS_OPTIONS,
).map((sentiment) => sentiment.value);

export const SOCIAL_LISTENING_DATERANGE_FILTER_DEFAULT = Object.values(
  SOCIAL_LISTENING_DATE_OPTIONS,
).map((date) => date.value);

export const SOCIAL_LISTENING_DEFAULT_FILTER_VALUES_DEPRECATED = Object.freeze({
  channels: SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT_DEPRECATED,
  sentiments: SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT,
  mediaTypes: SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT,
  dateRange: SOCIAL_LISTENING_DATERANGE_FILTER_DEFAULT[1],
  keyword: '',
  keywordTypes: SOCIAL_LISTENING_KEYWORDTYPE_FILTER_DEFAULT,
});

export const SOCIAL_LISTENING_DEFAULT_FILTER_VALUES = Object.freeze({
  channels: SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT,
  sentiments: SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT,
  mediaTypes: SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT,
  mentionTypes: SOCIAL_LISTENING_MENTIONTYPE_FILTER_DEFAULT,
  dateRange: SOCIAL_LISTENING_DATERANGE_FILTER_DEFAULT[1],
  keyword: '',
  keywordTypes: SOCIAL_LISTENING_KEYWORDTYPE_FILTER_DEFAULT,
});

export const TOP_POSTS_DEFAULT_QUERY_PARAMS = Object.freeze({
  ...SOCIAL_LISTENING_DEFAULT_FILTER_VALUES,
  dateRange: SOCIAL_LISTENING_DATERANGE_FILTER_DEFAULT[1],
});

export const SOCIAL_LISTENING_TOPIC_DATE_RANGE_OPTIONS = [
  { label: 'Last 24 Hours', value: 1 },
  { label: 'Last 3 Days', value: 2 },
  { label: 'Last 7 Days', value: 6 },
  { label: 'Last 4 Weeks', value: 27 },
];

export const POST_VOLUME_API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const POST_VOLUME_API_CAMEL_CASE_FORMAT = 'YYYYMMDDHH:mm:ss';
export const POST_VOLUME_GRAPH_OPTIONS = Object.freeze({
  POSTS_BY_CHANNEL_DEPRECATED: {
    value: 'POSTS_BY_CHANNEL',
    label: 'Posts by Channel',
    tooltip: 'The number of published posts within Trends broken down by channel',
    displayGraphLegend: true,
    topPostsFilterField: 'channels',
    primaryBreakdown: 'PLATFORM',
    secondaryBreakdown: 'SENTIMENT',
    chartDataKeys: Object.keys(SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED).map((o) =>
      o.toLowerCase(),
    ),
    chartConfig: {
      colour: {
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.INSTAGRAM.chartDataKey]:
          platformColors.instagram,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.LINKEDIN.chartDataKey]:
          platformColors.linkedin,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.REDDIT.chartDataKey]: platformColors.reddit,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.TIKTOK.chartDataKey]: platformColors.tiktok,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.WEB.chartDataKey]: CHART.SET.CHART_SET_20,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.TWITTER.chartDataKey]: platformColors.twitter,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.YOUTUBE.chartDataKey]: platformColors.youtube,
      },
      legendOptions: {
        INSTAGRAM: {
          label: 'Instagram',
          color: platformColors.instagram,
        },
        LINKEDIN: {
          label: 'LinkedIn',
          color: platformColors.linkedin,
        },
        REDDIT: {
          label: 'Reddit',
          color: platformColors.reddit,
        },
        TIKTOK: {
          label: 'TikTok',
          color: platformColors.tiktok,
        },
        WEB: {
          label: 'Web',
          color: CHART.SET.CHART_SET_20,
        },
        X: {
          label: 'X',
          color: platformColors.twitter,
        },
        YOUTUBE: {
          label: 'YouTube',
          color: platformColors.youtube,
        },
      },
      legendSort: {
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.INSTAGRAM.chartDataKey]: 1,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.LINKEDIN.chartDataKey]: 2,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.REDDIT.chartDataKey]: 3,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.TIKTOK.chartDataKey]: 4,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.WEB.chartDataKey]: 5,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.TWITTER.chartDataKey]: 6,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS_DEPRECATED.YOUTUBE.chartDataKey]: 7,
      },
      yAxisField: {
        name: 'totalPosts',
      },
      labelOverrides: {
        twitter: 'X',
        youtube: 'YouTube',
      },
      filterOverrides: {
        X: 'TWITTER',
      },
    },
  },
  POSTS_BY_CHANNEL: {
    value: 'POSTS_BY_CHANNEL',
    label: 'Posts by Channel',
    tooltip: 'The number of published posts within Trends broken down by channel',
    displayGraphLegend: true,
    topPostsFilterField: 'channels',
    primaryBreakdown: 'PLATFORM',
    secondaryBreakdown: 'SENTIMENT',
    chartDataKeys: Object.keys(SOCIAL_LISTENING_CHANNEL_OPTIONS).map((o) => o.toLowerCase()),
    chartConfig: {
      colour: {
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.FACEBOOK.chartDataKey]: platformColors.facebook,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.INSTAGRAM.chartDataKey]: platformColors.instagram,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.LINKEDIN.chartDataKey]: platformColors.linkedin,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.REDDIT.chartDataKey]: platformColors.reddit,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.TIKTOK.chartDataKey]: platformColors.tiktok,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.WEB.chartDataKey]: CHART.SET.CHART_SET_20,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.TWITTER.chartDataKey]: platformColors.twitter,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.YOUTUBE.chartDataKey]: platformColors.youtube,
      },
      legendSort: {
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.FACEBOOK.chartDataKey]: 1,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.INSTAGRAM.chartDataKey]: 2,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.LINKEDIN.chartDataKey]: 3,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.REDDIT.chartDataKey]: 4,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.TIKTOK.chartDataKey]: 5,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.WEB.chartDataKey]: 6,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.TWITTER.chartDataKey]: 7,
        [SOCIAL_LISTENING_CHANNEL_OPTIONS.YOUTUBE.chartDataKey]: 8,
      },
      legendOptions: {
        FACEBOOK: {
          label: 'Facebook',
          color: platformColors.facebook,
          channel: 'facebook',
        },
        INSTAGRAM: {
          label: 'Instagram',
          color: platformColors.instagram,
          channel: 'instagram',
        },
        LINKEDIN: {
          label: 'LinkedIn',
          color: platformColors.linkedin,
          channel: 'linkedin',
        },
        REDDIT: {
          label: 'Reddit',
          color: platformColors.reddit,
          channel: 'reddit',
        },
        TIKTOK: {
          label: 'TikTok',
          color: platformColors.tiktok,
          channel: 'tiktok',
        },
        WEB: {
          label: 'Web',
          color: CHART.SET.CHART_SET_20,
          channel: 'web',
        },
        X: {
          label: 'X',
          color: platformColors.twitter,
          channel: 'twitter',
        },
        YOUTUBE: {
          label: 'YouTube',
          color: platformColors.youtube,
          channel: 'youtube',
        },
      },
      yAxisField: {
        name: 'totalPosts',
      },
      labelOverrides: {
        twitter: 'X',
        youtube: 'YouTube',
      },
      filterOverrides: {
        X: 'TWITTER',
      },
    },
  },
  POSTS_BY_SENTIMENT: {
    value: 'POSTS_BY_SENTIMENT',
    label: 'Posts by Sentiment',
    tooltip: 'The breakdown of published posts within Trends by caption sentiment',
    displayGraphLegend: true,
    topPostsFilterField: 'sentiments',
    primaryBreakdown: 'SENTIMENT',
    chartConfig: {
      colour: {
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.value]: sentimentColors.positive,
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.value]: sentimentColors.neutral,
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.value]: sentimentColors.negative,
      },
      legendSort: {
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.value]: 1,
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.value]: 2,
        [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.value]: 3,
      },
      yAxisField: {
        name: 'percentOfPosts',
        format: 'percent',
      },
      labelOverrides: {
        positive: 'Positive Sentiment',
        neutral: 'Neutral Sentiment',
        negative: 'Negative Sentiment',
        percentofposts: '% of Posts',
      },
      axis: {
        y: {
          ticks: {
            format: {
              style: 'percent',
            },
          },
        },
      },
    },
  },
  POSTS_BY_MEDIA_TYPE: {
    value: 'POSTS_BY_MEDIA_TYPE',
    label: 'Posts by Media Type',
    tooltip: 'The breakdown of media type included in published posts within Trends',
    displayGraphLegend: true,
    topPostsFilterField: 'mediaTypes',
    primaryBreakdown: 'MEDIA_TYPE',
    secondaryBreakdown: 'SENTIMENT',
    chartDataKeys: Object.keys(SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS).map((o) => o.toLowerCase()),
    chartConfig: {
      colour: {
        PHOTOS: mediaTypeColors.photo,
        VIDEOS: mediaTypeColors.video,
        TEXT: mediaTypeColors.text,
        LINKS: mediaTypeColors.link,
      },
      legendSort: {
        PHOTOS: 1,
        VIDEOS: 2,
        LINKS: 3,
        TEXT: 4,
      },
      yAxisField: {
        name: 'totalPosts',
      },
      labelOverrides: {
        image: 'Photos',
        video: 'Videos',
        link: 'Links',
      },
      filterOverrides: {
        PHOTOS: 'IMAGE',
        VIDEOS: 'VIDEO',
        LINKS: 'LINK',
      },
    },
  },
  TOTAL_POSTS: {
    value: 'TOTAL_POSTS',
    label: 'Total Posts',
    tooltip: 'The total number of posts within Trends published during the selected date range',
    displayGraphLegend: false,
    primaryBreakdown: 'PLATFORM',
    chartConfig: {
      colour: {
        TOTALPOSTS: colours.ACTION.ACTION_500,
      },
      yAxisField: {
        name: 'totalPosts',
      },
      labelOverrides: {
        youtube: 'YouTube',
        anomalyTotalPosts: '',
        increaseOverPredictedVolume: '% Increase Over Predicted Volume',
      },
      disableTooltipLabelPrefix: true,
    },
  },
});

export const TOOLTIPS = {
  noUserAccessTitle: 'Looks Like You Don’t Have Permission To Use Social Listening',
  noUserAccessMessage:
    'Surface visual trends and competitors’ strategies, measure audience and UGC sentiment, and create easy-to-use reports. Contact your account admin to provide you access.',
};

export const SOCIAL_LISTENING_TOPIC_TOOLTIPS = {
  TOP_KEYWORDS: 'The most used keywords in post captions during the selected date range',
  SHARE_OF_VOICE_INDUSTRY:
    'The industry breakdown of brand posts within your Topic during the selected date range',
  SHARE_OF_VOICE_COMPETITOR:
    'The breakdown of competitor posts within your Topic during the selected date range',
  TOP_POSTS: 'A sample of the most engaging posts within this Topic during the reporting period',
  WEB_RESULTS: "The most recent web results that match your Topic's search criteria",
  VISUAL_TRENDS:
    'The most visually similar content segments, based on posts published during the selected date range',
  TOP_LINE_METRICS: {
    TOTAL_POSTS: 'The total number of posts within your Topic during the selected date range',
    UNIQUE_CONTRIBUTORS:
      'The unique number of accounts that have created posts within this Topic during the selected date range',
    TOTAL_ENGAGEMENTS:
      'The total number of engagements on posts within this Topic during the selected date range',
    AVERAGE_ENGAGEMENTS_PER_POST:
      'The average number of engagements per post within this Topic during the selected date range',
  },
  // Tooltip for Legacy Post Volume Charts
  POST_VOLUME: {
    POSTS_BY_CHANNEL: 'The number of published posts within your Topic, broken down by channel',
    POSTS_BY_SENTIMENT: 'The breakdown of published posts within your Topic by caption sentiment',
    POSTS_BY_MEDIA_TYPE:
      'The breakdown of media type included in published posts within your Topic',
    TOTAL_POSTS:
      'The total number of posts within your Topic published during the selected date range',
  },
  TOPIC_ALERT: {
    ALERT_SENSITIVITY:
      "Determine the Alert sensitivity for this Topic. Choosing 'Less Sensitive' will result in fewer spike notifications, and 'More Sensitive' will result in a higher number of spike notifications.",
    ALERT_RECIPIENTS:
      'Select which teammates will be emailed when the volume of Topic mentions meet the above criteria',
    SAVE_BUTTON_DISABLED: 'In order to save this alert, you must select a recipient',
  },
};

export const SOCIAL_LISTENING_TOPIC_TOOLTIPS_GDI = {
  VISUAL_TRENDS:
    'The most visually similar content segments within this Topic during the reporting period',
  WEB_RESULTS: "The most relevant and recent web results that match this Topic's search criteria",
  TOP_COMPETITORS:
    'The most active competitor accounts publishing posts within this Topic over the reporting period. This report is based on the competitors selected.',
  MENTIONS_BY_COMPETITORS:
    'The number of published posts from competitors within this Topic over time. This report is based on the competitors selected.',
  MENTIONS_TREND_TOPIC_PREVIEW:
    'A sample of posts matching your search criteria to analyze popularity over time',
  TOP_POST_TOPIC_PREVIEW:
    'A sample of the most engaging posts matching your search criteria during the reporting period',
  TRENDING_KEYWORD: 'The most used keywords in post captions during the reporting period',
  TOP_LOCATIONS:
    'The most frequent author locations within this Topic during the reporting period. Location is estimated based on public profile data when it is available for Facebook, Instagram, Web, X and Youtube.',
  POST_VOLUME: {
    POSTS_BY_CHANNEL:
      'The estimated number of published posts within this Topic over time by channel',
    POSTS_BY_SENTIMENT:
      'The number of published posts within this Topic over time by caption sentiment',
    POSTS_BY_MEDIA_TYPE:
      'The estimated number of published posts within this Topic over time by media type',
    MENTIONS_TREND: 'The estimated number of published posts within this Topic over time',
    NET_SENTIMENT_TREND:
      'The net sentiment of post captions within this Topic over time. Net sentiment shows the overall balance between positive and negative conversation in post captions.\n' +
      '\n' +
      'Net Sentiment = (Positive Captions - Negative Captions) / Total Captions *100',
  },
  DEMOGRAPHICS_GENDER: {
    TOOLTIP:
      "The percentage of published posts within this Topic by Gender. Gender is estimated based on public profile data when it is available for Facebook, Instagram, X and Youtube. Accounts that don't specify a gender, such as businesses, pets, accounts shared by multiple people, are classified as Other",
    NO_DATA: 'No posts found during this reporting period',
    NO_DATA_FOR_FILTERS:
      'No posts with gender match your filters. Adjust your filters to view more posts.',
  },
  DEMOGRAPHICS_AGE: {
    TOOLTIP:
      'The percentage of published posts within this Topic by Age. Age is estimated based on public profile data when it is available for Facebook, Instagram, X and Youtube.',
    NO_DATA: 'No posts found during this reporting period',
    NO_DATA_FOR_FILTERS:
      'No posts with age match your filters. Adjust your filters to view more posts.',
  },
};

export const SOCIAL_LISTENING_TREND_TOOLTIPS_GDI = {
  TOP_POSTS_PAGE:
    'The most engaging posts within the top 25 trending keywords during the reporting period',
  TOP_POST: 'The most engaging posts within this Trend published during the reporting period',
  TRENDING_KEYWORD: 'The top 25 trending keywords in post captions during the reporting period',
  POST_VOLUME: {
    POSTS_BY_CHANNEL: 'The number of published posts within this Trend over time by channel',
    POSTS_BY_SENTIMENT:
      'The number of published posts within this Trend over time by caption sentiment',
    POSTS_BY_MEDIA_TYPE: 'The number of published posts within this Trend over time by media type',
    MENTIONS_TREND: 'The number of published posts within this Trend over time',
  },
  STATS: {
    AVG_ENGAGEMENTS: 'The average number of engagements per post within this Visual Trend',
    POSITIVE_SENTIMENT:
      'The percentage of posts within this Visual Trend with a positive caption sentiment',
    NEGATIVE_SENTIMENT:
      'The percentage of posts within this Visual Trend with a negative caption sentiment',
  },
  VISUAL_TRENDS:
    'The most visually similar content segments from the top 25 trending keywords during the reporting period',
  VISUAL_TREND:
    'The most visually similar content segments within this Trend during the reporting period',
  TREND_CARD: {
    POSITIVE_SENTIMENT:
      'The percentage of posts within this Trend with a positive caption sentiment',
    NEUTRAL_SENTIMENT: 'The percentage of posts within this Trend with a neutral caption sentiment',
    NEGATIVE_SENTIMENT:
      'The percentage of posts within this Trend with a negative caption sentiment',
  },
};

export const TRENDING_KEYWORDS_DEFAULT_CONFIG = Object.freeze({
  title: 'Trending Keywords',
  tooltip: 'The top 25 trending keywords in post captions during the selected date range',
  chartKeyLabel: 'Keywords',
  ChartValueLabel: 'Total Posts',
  legend: {
    [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.value]: {
      value: true,
      label: 'Positive',
      color: SENTIMENT_COLORS.POSITIVE,
      disabled: false,
      canToggle: true,
    },
    [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.value]: {
      value: true,
      label: 'Neutral',
      color: SENTIMENT_COLORS.NEUTRAL,
      disabled: false,
      canToggle: true,
    },
    [SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.value]: {
      value: true,
      label: 'Negative',
      color: SENTIMENT_COLORS.NEGATIVE,
      disabled: false,
      canToggle: true,
    },
  },
});

export const SOCIAL_LISTENING_CONFIRMATION_DIALOG = {
  DELETE_TOPIC: {
    header: 'Delete Topic?',
    message:
      'This will permanently delete Topic data but will not reduce mentions already counted towards your monthly limit. This action cannot be undone.',
    acceptLabel: 'Delete',
  },
  CREATE_NEW_TOPIC: {
    header: 'Create New Topic?',
    message:
      'Creating a new Topic in Comparisons counts toward your Topic and Mention limits. If you would like to avoid creating new ones, you can select from your existing Topics.',
    acceptLabel: 'Continue',
  },
  EDIT_TOPIC_KEYWORDS: {
    header: 'Update Topic?',
    message: 'Updating the keywords will also be applied directly in that existing Topic',
    acceptLabel: 'Update',
  },
  EDIT_TOPIC_NAME: {
    header: 'Update Topic?',
    message: 'Updating the name will also be applied directly in that existing Topic',
    acceptLabel: 'Update',
  },
  CREATE_NEW_COMPARISON_WITH_NEW_TOPICS: {
    header: 'Create New Topics?',
    acceptLabel: 'Create',
    actionText: {
      edit: 'Update Comparison',
      create: 'Create Comparison',
    },
  },
};

export const SOCIAL_LISTENING_TOASTS = {
  DELETE_TOPIC: {
    ERROR: {
      severity: 'error',
      message: 'Topic failed to delete. Please refresh and try again.',
    },
  },
  DELETE_COMPARISON: {
    ERROR: {
      severity: 'error',
      message: 'Comparison failed to delete. Please refresh and try again.',
    },
  },
  CREATE_COMPARISON: {
    SUCCESS: {
      severity: 'success',
      message: 'Comparison created',
      life: 5000,
    },
    ERROR: {
      severity: 'error',
      message: 'Error Creating Topic Comparison',
      life: 5000,
    },
  },
  UPDATE_COMPARISON: {
    SUCCESS: {
      severity: 'success',
      message: 'Comparison updated',
      life: 5000,
    },
    ERROR: {
      severity: 'error',
      message: 'Error Updating Topic Comparison',
      life: 5000,
    },
  },
  CREATE_COMPARISON_TOPICS: {
    ERROR: {
      severity: 'error',
      message: 'Error while creating topics for the comparison',
      life: 5000,
    },
  },
  UPDATE_COMPARISON_TOPICS: {
    ERROR: {
      severity: 'error',
      message: 'Error while updating topic for the comparison',
      life: 5000,
    },
  },
};

export const SOCIAL_LISTENING_TOPIC_DROPDOWN_FILTER_OPTIONS = [
  { label: 'Media Type', value: SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE },
  { label: 'Channel', value: SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL },
  { label: 'Sentiment', value: SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT },
];

export const SOCIAL_LISTENING_TOPIC_FILTER_VALUE_MAP = Object.freeze({
  [SOCIAL_LISTENING_FILTERS.FILTER_VISUAL]: {
    label: '',
    field: SOCIAL_LISTENING_FILTERS.FILTER_VISUAL,
    emptyOperator: true,
    value: null,
    props: {
      allowEmpty: true,
      minimalFilterStyle: true,
      disableOpenOnMountIfValueNull: true,
      disableOutsideClick: true,
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE]: {
    label: SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT.join(', '),
    field: SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE,
    value: SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT,
    operator: 'is',
    props: {
      enforceMinimumCheckbox: true,
      disableOpenOnMountIfValueNull: true,
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL]: {
    label: '',
    field: SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL,
    imgFilter: true,
    emptyOperator: true,
    valueToIconMap: Object.fromEntries(
      Object.values(SOCIAL_LISTENING_CHANNEL_OPTIONS).map((channel) => [
        channel.value,
        channel.iconName,
      ]),
    ),
    value: SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT,
    props: {
      enforceMinimumCheckbox: true,
      minimalFilterStyle: true,
      disableOpenOnMountIfValueNull: true,
    },
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT]: {
    label: '',
    field: SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT,
    value: SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT,
    operator: 'is',
    props: {
      disableOpenOnMountIfValueNull: true,
    },
  },
});

export const SOCIAL_LISTENING_TOPIC_FILTER_WIDGETS = Object.freeze({
  [SOCIAL_LISTENING_FILTERS.FILTER_VISUAL]: {
    name: 'VisualFilterSelectionWidget',
    label: 'Visual Filter',
    icon: 'eyeOpen',
    noDuplicate: true,
    disableDelete: true,
    showNumberBadge: false,
    type: FILTER_TYPE.VISUAL.value,
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE]: {
    name: 'MediaFilterCheckboxWidget',
    label: 'Media Type',
    icon: 'media',
    noDuplicate: true,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS).map((type) => ({
        label: type.title,
        value: type.value,
      })),
    },
    type: FILTER_TYPE.MEDIA_TYPES.value,
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL]: {
    name: 'MediaFilterCheckboxWidget',
    label: 'Channel',
    noDuplicate: true,
    options: {
      filterOptions: formatChannelFilterOptions(Object.values(SOCIAL_LISTENING_CHANNEL_OPTIONS)),
    },
    type: FILTER_TYPE.CHANNELS.value,
  },
  [SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT]: {
    name: 'MediaFilterCheckboxWidget',
    label: 'Sentiment',
    noDuplicate: true,
    options: {
      filterOptions: Object.values(SOCIAL_LISTENING_SENTIMENTS_OPTIONS).map((sentiment) => ({
        label: sentiment.title,
        icon: {
          name: sentiment.iconName,
          color: sentiment.iconColor,
        },
        value: sentiment.value,
      })),
    },
    type: FILTER_TYPE.SENTIMENTS.value,
  },
});

export const SOCIAL_LISTENING_SEARCH_BODY_MAP = Object.freeze({
  [SOCIAL_LISTENING_FILTERS.FILTER_CHANNEL]: 'sources',
  [SOCIAL_LISTENING_FILTERS.FILTER_MEDIA_TYPE]: 'mediaTypes',
  [SOCIAL_LISTENING_FILTERS.FILTER_VISUAL]: 'visualData',
  [SOCIAL_LISTENING_FILTERS.FILTER_SENTIMENT]: 'sentiment',
});

export const TOPIC_BUILDER_STEP_DESCRIPTIONS = Object.freeze({
  AUDIENCE: 'Select which type of Topic you want to create',
  WEB_SOURCE: 'Would you like to include web results?',
  BRAND: {
    COMPETITOR: "Which of your brands' competitor lists do you want to choose from?",
    INDUSTRY: 'Which brand would you like to include in your Industry Topic?',
  },
  KEYWORD:
    'Refine your Topic results by adding keywords, hashtags, and @mention combinations to listen to',
  NAME: 'Name your Topic',
});

export const TOPIC_BUILDER_TOAST_MESSAGES = {
  success: 'Topic saved',
  error: 'Failed to save. Please try again.',
};

export const EDIT_TOPIC_KEYWORD_TOAST_MESSAGES = {
  SAVED: 'Successfully Saved',
};

export const metricsMap = {
  FACEBOOK: {
    engagementRate: {
      label: 'Engagement Rate',
      tooltip:
        'The percentage of followers that engaged with this post\n' +
        'Engagement Rate = Engagements / Followers',
      field: 'engagementRate',
      format: metricFormats.PERCENT,
    },
    engagements: {
      label: 'Engagements',
      tooltip:
        'The total number of engagements this post received\n' +
        'Engagements = Reactions + Comments + Shares',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    likes: {
      label: 'Likes',
      tooltip: 'The number of likes this post has received',
      field: 'likes',
      format: metricFormats.NUMBER,
    },
    comments: {
      label: 'Comments',
      tooltip: 'The number of comments this post has received',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  INSTAGRAM: {
    comments: {
      label: 'Comments',
      tooltip: 'The number of comments this post has received',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    engagementRate: {
      label: 'Engagement Rate',
      tooltip: {
        content: 'The percentage of followers that engaged with this post',
        footer: 'Engagement Rate = Engagements / Followers',
      },
      field: 'engagementRate',
      format: metricFormats.PERCENT,
    },
    engagementRateViewsPublic: {
      label: 'Engagement Rate (Views)',
      tooltip: {
        content: 'The percentage of users who engaged with this post after seeing it',
        footer: 'Engagement Rate (Views) = Engagements / Views',
      },
      unavailableTooltip: {
        content:
          'The percentage of users who engaged with this post after seeing it\n\nEngagement Rate (Views) is unavailable for this Reel due to missing views data',
        footer: 'Engagement Rate (Views) = Engagements / Views\n',
      },
      field: 'engagementRateViewsPublic',
      format: metricFormats.PERCENT,
    },
    engagements: {
      label: 'Engagements',
      tooltip: {
        content: 'The total number of engagements this post received',
        footer: 'Engagements = Likes + Comments',
      },
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    likes: {
      label: 'Likes',
      tooltip: 'The number of likes this post has received',
      field: 'likes',
      format: metricFormats.NUMBER,
    },
    publicViews: {
      label: 'Views',
      tooltip:
        'The number of times this video was played or replayed for 1ms or more. ' +
        'Replays are counted after the initial play in the same video session.',
      unavailableTooltip:
        'The number of times this video was played or replayed for 1ms or more. ' +
        'Replays are counted after the initial play in the same video session.\n\n' +
        'Views metric is not available for this Reel due to Instagram API limitations.',
      field: 'publicViews',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  WEB: {
    engagements: {
      label: 'Engagements',
      tooltip: 'The total number of engagements this post received',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  LINKEDIN: {
    engagements: {
      label: 'Engagements',
      tooltip:
        'The total number of comments this post received at the time it was imported. Comments for LinkedIn are not updated in real time.',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    comments: {
      label: 'Comments',
      tooltip:
        'The number of comments this post received at the time it was imported. Comments for LinkedIn are not updated in real time.',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  TIKTOK: {
    engagements: {
      label: 'Engagements',
      tooltip:
        'The total number of engagements this post received at the time it was imported. Engagements for TikTok are not updated in real time.\n' +
        'Engagements = Likes + Comments',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    likes: {
      label: 'Likes',
      tooltip:
        'The number of likes this post has received at the time it was imported. Likes for TikTok are not updated in real time.',
      field: 'likes',
      format: metricFormats.NUMBER,
    },
    comments: {
      label: 'Comments',
      tooltip:
        'The number of comments this post has received at the time it was imported. Comments for TikTok are not updated in real time.',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  REDDIT: {
    engagements: {
      label: 'Engagements',
      tooltip:
        'The total number of engagements this post received at the time it was imported. Engagements for Reddit are not updated in real time.\n' +
        'Engagements = Votes + Comments',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    comments: {
      label: 'Comments',
      tooltip:
        'The number of comments this post received at the time it was imported. Comments for Reddit are not updated in real time.',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    votes: {
      label: 'Votes',
      tooltip:
        'The number of votes this post received at the time it was imported. Votes for Reddit are not updated in real time.',
      field: 'votes',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  TWITTER: {
    publicEngagements: {
      label: 'Public Engagements',
      tooltip:
        'The total number of public engagements this post received\n' +
        'Engagements = Likes + Reposts + Replies + Quote Posts',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    replies: {
      label: 'Replies',
      tooltip: 'The number of replies this post has received',
      field: 'replies',
      format: metricFormats.NUMBER,
    },
    totalRetweets: {
      label: 'Total Reposts',
      tooltip: 'The total number of reposts received\nTotal Reposts = Reposts + Quote Posts',
      field: 'totalRetweets',
      format: metricFormats.NUMBER,
    },
    retweets: {
      label: 'Reposts',
      tooltip: 'The number of reposts received',
      field: 'retweets',
      format: metricFormats.NUMBER,
    },
    quoteTweets: {
      label: 'Quote Posts',
      tooltip: 'The number of quotes posts received',
      field: 'quoteTweets',
      format: metricFormats.NUMBER,
    },
    likes: {
      label: 'Likes',
      tooltip: 'The number of likes this post has received',
      field: 'likes',
      format: metricFormats.NUMBER,
    },
    videoViews: {
      label: 'Video Views',
      tooltip: 'The number of video views this post has received',
      field: 'videoViews',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  YOUTUBE: {
    videoViews: {
      label: 'Video Views',
      tooltip: 'The number of video views this post has received',
      field: 'videoViews',
      format: metricFormats.NUMBER,
    },
    likes: {
      label: 'Likes',
      tooltip: 'The number of likes this post has received',
      field: 'likes',
      format: metricFormats.NUMBER,
    },
    comments: {
      label: 'Comments',
      tooltip: 'The number of comments this post has received',
      field: 'comments',
      format: metricFormats.NUMBER,
    },
    engagements: {
      label: 'Engagements',
      tooltip: 'The total number of engagements this post received\nEngagements = Likes + Comments',
      field: 'engagements',
      format: metricFormats.NUMBER,
    },
    mentionType: {
      label: 'Mention Type',
      tooltip:
        'The content format of this mention such as audio, blog, caption, forum or news article',
      field: 'mentionType',
      format: metricFormats.STRING,
      severity: 'secondary',
    },
  },
  SENTIMENT_TOOLTIP: 'The sentiment of this post’s caption',
};

export const VISUAL_FILTER_PAGES = {
  TOP_PERFORMING: 'topPerforming',
  TRENDS: 'trends',
  LIBRARY: 'library',
};

export const VISUAL_DATA_TYPES = {
  UPLOAD: 'data',
  TRENDS: 'sourceAndId',
  LIBRARY: 'libraryMediaDetails',
};

export const VISUAL_FILTER_MAX_FILE_SIZE_MB = 30;

export const OPERATOR_MAP = {
  OR: 'OR',
  AND: 'AND',
  'AND NOT': 'AND~',
  NOT: 'NOT',
};

export const OPERATOR_LABEL_BY_TYPE = {
  [OPERATOR_MAP.OR]: 'Or',
  [OPERATOR_MAP.AND]: 'And',
  [OPERATOR_MAP['AND NOT']]: 'And',
  [OPERATOR_MAP.NOT]: 'Not',
};

export const KEYWORD_GROUP_TYPE = {
  INCLUDES: 'Includes',
  EXCLUDES: 'Excludes',
};

export const DEFAULT_OPERATOR_BY_GROUP_TYPE = {
  [KEYWORD_GROUP_TYPE.INCLUDES]: OPERATOR_MAP.AND,
  [KEYWORD_GROUP_TYPE.EXCLUDES]: OPERATOR_MAP.AND,
};

export const OPERATOR_GROUP_TYPE = {
  [OPERATOR_MAP.AND]: 'And',
  [OPERATOR_MAP.OR]: 'Or',
};

export const DUPLICATE_KEYWORD_ERROR_COPY = 'Keyword already added';
export const TOO_MANY_KEYWORD_ERROR_COPY = `Topics can not exceed ${TOPIC_MAX_KEYWORD_COUNT} keywords`;
export const MAX_ONE_SPACE_ERROR_COPY = 'Only one space allowed between words';
export const NO_SPECIAL_CHAR_ERROR_COPY = 'Special characters are not supported';
export const keywordLengthReachedError = (limit) => `Keywords can not exceed ${limit} characters`;

export const TOPIC_BUILDER_VALIDATION_ERROR_MESSAGES = {
  name: {
    required: 'This field is required',
    minLength: 'Topic name must contain at least 3 characters',
    nameExists: 'A Topic with this name already exists',
  },
};

export const SOCIAL_LISTENING_EVENT_NAMES = {
  SOCIAL_LISTENING_REPORT_INTERACTION: 'Social Listening Report Interaction',
  SOCIAL_LISTENING_FILTERS: 'Social Listening Filters',
  UPGRADE_NOW_CLICKED: 'Upgrade Now Clicked',
  TRENDING_KEYWORD_SELECTED: 'Trending Keywords Selected',
  VISUAL_TRENDS_POST_CLICKED: 'Visual Trends Post Clicked',
  MEDIA_POPUP_OPENED: 'Media Pop-up Opened',
  TOPIC_CREATION_STARTED: 'Topic Creation Started',
  TOPIC_WEB_RESULTS_NEXT: 'Topic Web Results Next',
  TOPIC_BRAND_NEXT: 'Topic Brand Next',
  TOPIC_INDUSTRY_NEXT: 'Topic Industry Next',
  TOPIC_DATA_SOURCE_SELECTED: 'Topic Data Source Selected',
  TOPIC_COMPETITORS_ADDED: 'Topic Competitors Added',
  TOPIC_KEYWORDS_ADDED: 'Topic Keywords Added',
  TOPIC_CREATED: 'Topic Created',
  TOPIC_CREATION_FAILED: 'Topic Creation Failed',
  TOPIC_DELETED: 'Topic Deleted',
  TOPIC_KEYWORDS_EDITED: 'Topic Keywords Edited',
  TOPIC_SETTINGS_CHANGED: 'Topic Settings Changed',
  MANAGE_USE_CLICKED: 'Manage Use Clicked',
  TOPIC_VIEWED: 'Topic Viewed',
  COMPARISON_VIEWED: 'Comparison Viewed',
  COMPARISON_DELETED: 'Comparison Deleted',
  COMPARISON_CREATION_STARTED: 'Comparison Creation Started',
  COMPARISON_TOPICS_ADDED: 'Comparison Topics Added',
  COMPARISON_CREATED: 'Comparison CREATED',
  SOCIAL_LISTENING_CARD_CLICKED: 'Social Listening Card Clicked',
  SOCIAL_LISTENING_VIEWED_REPORT_IN_APP: 'Social Listening - Viewed Report in App',
  MANAGE_USAGE_UPGRADE_LIMITS: 'Manage Use Clicked - Upgrade Limits',
  TOPIC_TEXT_EDITOR_SELECTED: 'Topic Text Editor Selected',
  SUGGESTED_TOPIC_HOVERED: 'Suggested Topic Hovered',
  TOPIC_QUICK_SEARCH_PREVIEW: 'Topic Quick Search Preview',
};

export const TOPIC_NAME_MAX_LENGTH = 50;
export const TOPIC_MAX_COMPETITORS_AUTO_SELECT = 30;

export const SOCIAL_LISTENING_REPORT_SECTIONS = {
  TOP_PERFORMING_POST: 'Top Performing Posts',
  TOP_KEYWORDS: 'Top Keywords',
  SHARE_OF_VOICE_INDUSTRY: 'Share of Voice by Industry',
  SHARE_OF_VOICE_COMPETITOR: 'Share of Voice by Competitor',
  SHARE_OF_VOICE: 'Share of Voice',
  TOTAL_POSTS: 'Total Posts Line Graph',
  VISUAL_TRENDS: 'Visual Trends',
  SOCIAL_TRENDS: 'Social Trends',
  TOPIC: 'Social Topic',
  WEB_RESULTS: 'Web Results',
  QUICK_SEARCH: 'Quick Search',
  MENTIONS_BREAKDOWN: 'Mentions Breakdown',
};

export const SOCIAL_LISTENING_VISUAL_FILTER_TYPES = {
  TRENDS: 'Trends',
  LIBRARY: 'Library',
  UPLOAD_MEDIA: 'Upload media',
  TOP_PERFORMING: 'Top Performing Posts',
};

export const SOCIAL_LISTENING_REPORT_INTERACTION_TYPES = {
  KEYWORD_CLICKED: 'Keyword Clicked',
  KEYWORD_FILTER_CHANGED: 'Keyword Filter Changed',
  DATA_POINT_CLICKED: 'Data Point Clicked',
  LEGEND_CHANGED: 'Changed Legend',
  GRAPH_METRIC_CHANGED: 'Changed Graph Metric',
  INDUSTRIES_CHANGED: 'Industries Changed',
  COMPETITORS_CHANGED: 'Competitors Changed',
};

export const TOPIC_EDIT_CONTEXT = {
  TOPIC_NAME: 'Topic name',
  KEYWORDS: 'Keywords',
  EXCLUDED_HANDLES: 'Excluded Handles',
  SOURCE: 'Source',
};

export const MANAGE_USAGE_POPUP_CLICK_LOCATION = {
  NAV_BAR: 'Navigation Bar',
  BANNER: 'Banner',
  DIRECT_LINK: 'Direct Link',
};

export const MANAGE_USAGE_CLICK_LOCATION_GDI = {
  OVERVIEW: 'Overview Page',
  TOPIC: 'Topic Page',
  COMPARISON: 'Comparison Page',
};

export const SOCIAL_LISTENING_CARD_TYPES = {
  TOP_POST: 'Top Post',
  VISUAL_TREND: 'Visual Trend',
  WEB_RESULT: 'Web Result',
};

export const FILTER_DETAILS = {
  [FILTER_TYPE.CHANNELS.value]: {
    filter: FILTER_TYPE.CHANNELS.text,
    action: 'changed channel',
    default: SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT,
  },
  [FILTER_TYPE.SENTIMENTS.value]: {
    filter: FILTER_TYPE.SENTIMENTS.text,
    action: 'changed sentiment',
    default: SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT,
  },
  [FILTER_TYPE.MEDIA_TYPES.value]: {
    filter: FILTER_TYPE.MEDIA_TYPES.text,
    action: 'changed media type',
    default: SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT,
  },
  [FILTER_TYPE.MENTION_TYPES.value]: {
    filter: FILTER_TYPE.MENTION_TYPES.text,
    action: 'changed mention type',
    default: SOCIAL_LISTENING_MENTIONTYPE_FILTER_DEFAULT,
  },
  [FILTER_TYPE.KEYWORD_TYPE.value]: {
    filter: FILTER_TYPE.KEYWORD_TYPE.text,
    action: 'changed keyword type',
    default: SOCIAL_LISTENING_KEYWORDTYPE_FILTER_DEFAULT,
  },
  [FILTER_TYPE.DATE_RANGE.value]: {
    filter: FILTER_TYPE.DATE_RANGE.text,
    action: 'changed date',
  },
  [FILTER_TYPE.SORT_BY.value]: {
    filter: FILTER_TYPE.SORT_BY.text,
    action: 'sorted',
  },
  [FILTER_TYPE.VISUAL.value]: {
    filter: FILTER_TYPE.VISUAL.text,
    action: 'changed visual',
  },
  [FILTER_TYPE.KEYWORD.value]: {
    filter: FILTER_TYPE.KEYWORD.text,
    action: 'changed keyword',
  },
};

export const SOCIAL_LISTENING_TOPIC_CREATION_TOOLTIPS = {
  RESULT_PREVIEW_HEADER:
    'The estimated results matching your search criteria during the reporting period',
  EXCLUDE_HANDLES_HEADER:
    'Enter handles to exclude their posts from this Topic’s results. Handles must be an exact match. ',
  HANDLES_COPIED_TOAST: 'Handles copied to clipboard',
  HANDLES_COPIED_TOAST_ERROR: 'Failed to copy handles to clipboard',
};

export const SOCIAL_LISTENING_TOPIC_CREATION_FLOW = {
  CREATE_TOPIC: 'Create Topic',
  SUGGESTED_TOPIC: 'Suggested Topic',
  QUICK_SEARCH: 'Quick Search',
  BRAND_HEALTH: 'Brand Health',
};

export const SOCIAL_LISTENING_TOPIC_PREVIEW_SEARCH_TYPE = {
  quickSearch: 'Quick Search',
  keywordBuilder: 'Keyword Builder',
  textEditor: 'Plain Text Editor',
};

export const SOCIAL_LISTENING_TOPIC_CREATOR_TAB_VALUES = {
  QUICK_SEARCH: 'quickSearch',
  KEYWORD_BUILDER: 'keywordBuilder',
  PLAIN_TEXT_EDITOR: 'textEditor',
};

export const NO_DATA_FOUND = 'No posts found during this reporting period';
export const NO_DATA_FOUND_FOR_FILTERS =
  'No posts matching your filters. Adjust your filters to view more posts';
export const NO_LOCATION_DATA_FOR_FILTERS =
  'No posts with location match your filters. Adjust your filters to view more posts.';
export const NO_DATA_FOUND_INDUSTRY = 'There were no Industry posts during this time period';
export const NO_KEYWORDS_FOUND = 'No top keywords during this reporting period';
export const NO_WEB_RESULTS = 'No web results found for your Topic';
export const NO_WEB_RESULTS_FOR_FILTER =
  'No web results match your filters. Adjust your filters to view more web results.';
export const NO_VISUAL_TRENDS = 'No Visual Trends to display for this time period';

export const NULL_USERNAME = 'User';

export const TOPIC_BUILDER_FORM_TYPES = {
  BRAND: 'BRAND',
  COMPETITOR: 'COMPETITOR',
};

export const SOCIAL_LISTENING_OVERVIEW_SOV_CHART_OPTIONS = {
  title: 'Share of Voice By Industry',
  tooltip:
    'The breakdown of industry handles that used the top 25 keywords and hashtags within the selected date range',
  breakdown: 'INDUSTRY',
};

export const mentionLimitBannerMessage = (limit) =>
  `You've hit your limit of ${limit} mentions. Listening has been paused on all Topics. Manage Limits to avoid missing important mentions.`;

export const ILLEGAL_KEYWORD_SPECIAL_CHARACTERS = /[(){}[\]~"%]|#\s/;

export const WEB_SEARCH_SOURCES = Object.freeze({
  WEB: null,
  REDDIT: 'reddit.com',
  LINKEDIN: 'linkedin.com/posts',
});

export const WEB_SEARCH_SOURCE_OPTIONS_WITHOUT_LINKEDIN = Object.freeze({
  [WEB_SEARCH_SOURCES.WEB]: {
    text: 'Web',
    value: WEB_SEARCH_SOURCES.WEB,
    iconName: 'web',
  },
  [WEB_SEARCH_SOURCES.REDDIT]: {
    text: 'Reddit',
    value: WEB_SEARCH_SOURCES.REDDIT,
    iconName: 'reddit',
  },
});

export const WEB_SEARCH_SOURCE_OPTIONS = Object.freeze({
  [WEB_SEARCH_SOURCES.WEB]: {
    text: 'Web',
    value: WEB_SEARCH_SOURCES.WEB,
    iconName: 'web',
  },
  [WEB_SEARCH_SOURCES.LINKEDIN]: {
    text: 'LinkedIn',
    value: WEB_SEARCH_SOURCES.LINKEDIN,
    iconName: 'linkedin',
  },
  [WEB_SEARCH_SOURCES.REDDIT]: {
    text: 'Reddit',
    value: WEB_SEARCH_SOURCES.REDDIT,
    iconName: 'reddit',
  },
});

export const WEB_SEARCH_SOURCE_OPTIONS_GDI = Object.freeze({
  [WEB_SEARCH_SOURCES.WEB]: {
    text: 'Web',
    value: WEB_SEARCH_SOURCES.WEB,
    iconName: 'www',
  },
  [WEB_SEARCH_SOURCES.LINKEDIN]: {
    text: 'LinkedIn',
    value: WEB_SEARCH_SOURCES.LINKEDIN,
    iconName: 'linkedin-color',
  },
  [WEB_SEARCH_SOURCES.REDDIT]: {
    text: 'Reddit',
    value: WEB_SEARCH_SOURCES.REDDIT,
    iconName: 'reddit',
  },
});

export const SOV_GRAPH_METRIC_OPTIONS = Object.freeze({
  POST_VOLUME: {
    value: 'POST_VOLUME',
    label: 'Total Posts',
    aggregationMetric: null,
    property: 'postVolume',
    postfix: 'Posts',
    optionLabel: 'Mentions',
  },
  ENGAGEMENTS: {
    value: 'ENGAGEMENTS',
    label: 'Total Engagements',
    aggregationMetric: 'ENGAGEMENTS',
    property: 'postEngagement',
    postfix: 'Engagements',
    optionLabel: 'Engagements',
  },
});
export const TOP_LOCATIONS_METRIC_OPTIONS = Object.freeze({
  COUNTRY: {
    value: 'COUNTRY',
    optionLabel: 'Countries',
  },
  CITY: {
    value: 'CITY',
    optionLabel: 'Cities',
  },
  ADMINISTRATIVE_DIVISION: {
    value: 'ADMINISTRATIVE_DIVISION',
    optionLabel: 'States/Provinces',
  },
});
export const DEMOGRAPHICS_AGE_GENDER_METRIC_OPTIONS = Object.freeze({
  ALL: {
    value: 'all',
    optionLabel: 'All',
  },
  MALE: {
    value: 'male',
    optionLabel: 'Male',
  },
  FEMALE: {
    value: 'female',
    optionLabel: 'Female',
  },
});

export const DEFAULT_KEYWORD_GROUPS = Object.freeze([
  {
    level: 0,
    keywords: [],
    operators: ['or'],
  },
]);

export const SHOW_ALL_BREAKDOWN_THRESHOLD = 0.005;

export const SOCIAL_LISTENING_TOUR_URL =
  'https://www.dashhudson.com/interactive-demo/social-listening-product-demo?utm_medium=referral&utm_source=dh_platform&utm_campaign=sl_upgrade';

export const ALERT_SENSITIVITY = {
  MORE_SENSITIVE: 'MORE_SENSITIVE',
  BALANCED: 'BALANCED',
  LESS_SENSITIVE: 'LESS_SENSITIVE',
};

export const ALERT_SENSITIVITY_THRESHOLDS = {
  // don't change order
  [ALERT_SENSITIVITY.MORE_SENSITIVE]: 90,
  [ALERT_SENSITIVITY.BALANCED]: 95,
  [ALERT_SENSITIVITY.LESS_SENSITIVE]: 99,
};

export const ALERT_SENSITIVITY_LABEL_BY_NUMBER = invert(ALERT_SENSITIVITY_THRESHOLDS);

export const ALERT_CHART_FOOTER_MESSAGES = {
  [ALERT_SENSITIVITY.LESS_SENSITIVE]:
    'When "Less Sensitive" is selected, Dash Social will detect fewer spikes in mention volume. Graphic is for illustrative purposes only.',
  [ALERT_SENSITIVITY.MORE_SENSITIVE]:
    'When "More Sensitive" is selected, Dash Social will detect more spikes in mention volume. Graphic is for illustrative purposes only.',
  [ALERT_SENSITIVITY.BALANCED]:
    'When "Balanced" is selected, Dash Social will detect a moderate number of spikes in mention volume. Graphic is for illustrative purposes only.',
};

export const ALERT_SENSITIVITY_OPTIONS = Object.freeze({
  [ALERT_SENSITIVITY.LESS_SENSITIVE]: {
    value: ALERT_SENSITIVITY.LESS_SENSITIVE,
    label: 'Less Sensitive',
    threshold: 99,
  },
  [ALERT_SENSITIVITY.BALANCED]: {
    value: ALERT_SENSITIVITY.BALANCED,
    label: 'Balanced',
    threshold: 95,
  },
  [ALERT_SENSITIVITY.MORE_SENSITIVE]: {
    value: ALERT_SENSITIVITY.MORE_SENSITIVE,
    label: 'More Sensitive',
    threshold: 90,
  },
});

export const ALERT_HEADER_TOPIC_CREATED =
  'Creating your Topic. Results may take up to 24 hours to populate.';
export const ALERT_HEADER_TOPIC_UPDATED =
  'Updating your Topic. Results may take up to 24 hours to populate.';

export const SOCIAL_LISTENING_LOADED_SOURCE = {
  APP: 'APP',
  ALERTS_EMAIL: 'ALERTS_EMAIL',
  UPDATES_EMAIL: 'UPDATES_EMAIL',
  VISION_AI: 'VISION_AI',
  REPORT: 'REPORT',
};

export const ALERT_EVENT_NAMES = {
  SOCIAL_LISTENING_ALERT_SETTINGS_CHANGED: 'Social Listening Alert Settings Changed',
  SOCIAL_LISTENING_TOPIC_ALERT_ACTIVATED: 'Social Listening Topic Alert Activated',
  SOCIAL_LISTENING_TOPIC_ALERT_CREATED: 'Social Listening Topic Alert Created',
  SOCIAL_LISTENING_TOPIC_ALERT_DEACTIVATED: 'Social Listening Topic Alert Deactivated',
};

export const KEYWORD_WEIGHTS = {
  SINGLE_KEYWORD: '400',
  MULTI_KEYWORD: '500',
  HOVER_ANY_KEYWORD: '700',
};

export const TRUNCATED_KEYWORD_LENGTH = {
  KEYWORDS: 30,
};

export const KEYWORD_CHIP_TRUNCATE_WIDTH = {
  PIXELS: '320',
};

export const SOCIAL_LISTENING_PAGES = {
  VIEW_YOUR_TOPIC: 'Social Listening - View Your Topic',
  TRENDS: 'Social Listening - Overview',
  CREATE_TOPIC_POPUP: 'Social Listening - Create Topic Popup',
  TEAM_TOPICS: 'Social Listening - Team Topics',
};

export const TOPICS_QUERY_PARAM_MAP = {
  SOURCES: 'sources',
  MEDIA_TYPES: 'mediaTypes',
  POSITIVE_SENTIMENT: 'includePositive',
  NEGATIVE_SENTIMENT: 'includeNegative',
  NEUTRAL_SENTIMENT: 'includeNeutral',
  VISUAL_DATA_INCLUDE: 'includeVisualDataSourceAndId',
  VISUAL_DATA_EXCLUDE: 'excludeVisualDataSourceAndId',
  INDUSTRY_IDS: 'industryIds',
  SOURCE_AND_CREATOR_IDS: 'sourceAndCreatorIds',
  MENTION_TYPES: 'mentionTypes',
};

export const MAX_TOPIC_KEYWORD_LENGTH = 100;

export const KEYWORD_TEXT_EDITOR_ERRORS = {
  MISSING_STARTING_BRACKET: 'Missing starting bracket',
  MISSING_CLOSING_BRACKET: 'Missing closing bracket',
  UNEXPECTED_CLOSING_BRACKET: 'Unexpected closing bracket',
  UNSUPPORTED_CHARACTER: 'Unsupported special character(s)',
  MAX_LENGTH_EXCEEDED: 'Topic keywords can not exceed 100 characters',
  MULTIPLE_OPERATORS: 'Unsupported combination of operators ("AND", "OR" or "NOT")',
  MULTIPLE_NOT_CLAUSES: 'Only a single "NOT" clause is supported',
};

export const nestingDepthExceededError = (keyword) => {
  const keywordSnippet = keyword ? ` found near "${keyword}"` : '';
  return `Too many nested conditions${keywordSnippet}. Simplify by reducing the number of nested conditions`;
};

export const notClauseContainsAndError = (keyword) => {
  const keywordSnippet = keyword ? ` Found near "${keyword}".` : '';
  return `"NOT" conditions may only contain the "OR" operator.${keywordSnippet}`;
};

export const keywordDuplicateError = (keyword) => `Keyword "${keyword}" already exists`;

export const SOCIAL_LISTENING_OVERVIEW_TOOLTIPS = {
  QUICK_SEARCH:
    'Create a Quick Search using a combination of keywords and capitalized operators (AND, OR, NOT) to view posts from the last 4 weeks. Quick Searches can be saved as Topics.',
  SUGGESTED_TOPICS: "Vision AI-suggested Topics are based on your brand's most recent content",
};

export const SOCIAL_LISTENING_QUICK_SEARCH_TOOLTIPS = {
  SEARCH_RESULT_CHANNELS: 'Instagram, X and YouTube results are part of the search results below',
  TOP_POSTS: 'The most engaging posts matching your search criteria during the reporting period',
  POST_VOLUME: 'A sample of posts matching your search criteria to analyze popularity over time',
  TOP_LINE_METRICS: {
    TOTAL_POSTS:
      'The estimated number of posts matching your search criteria during the reporting period',
    UNIQUE_CONTRIBUTORS:
      'The unique number of accounts that have created posts within this Topic during the selected date range',
    AVERAGE_ENGAGEMENTS_PER_POST:
      'The average number of engagements on posts matching your search criteria during the reporting period',
    AVERAGE_POSTS_PER_DAY:
      'The estimated daily posts matching your search criteria during the reporting period',
  },
};

export const BOOLEAN_EXPRESSION_CODE_EDITOR_PLACEHOLDER =
  'Enter your keywords, hashtags or @mentions combinations. Eg. Bedroom AND (blankets OR pillows)';

export const OVERVIEW_SUGGESTED_TOPICS_EMPTY_STATE = {
  HEADER: 'Start Posting to Generate Suggested Topics',
  DESCRIPTION:
    'You need a minimum of 5 published posts on your account for suggested Topics to appear.',
};

export const TOPIC_BUILDER_FORM_STATE_FILTERS = {
  webSourceIncluded: false,
  webSource: null,
  brandIds: [],
  competitorIds: [],
  industryIds: [],
  keywords: [
    {
      keywords: [],
      level: 0,
      operators: ['or'],
    },
  ],
  keywordBooleanExpression: '',
};

export const QUICKSEARCH_POST_TREND_PREVIEW_OPTIONS = {
  label: 'Post Trend Preview',
};

export const KEYWORD_EDITOR_CHAR_TO_CLASS = {
  '(': '.paren-left',
  ')': '.paren-right',
  AND: '.operator-and',
  OR: '.operator-or',
  NOT: '.operator-not',
};

export const SOCIAL_LISTENING_TOP_LINE_STATS_PREVIEW = {
  MENTIONS: {
    label: 'Mentions',
    tooltip:
      'The estimated number of posts matching your search criteria during the reporting period',
    type: 'Total',
  },
  AVERAGE_MENTIONS_PER_DAY: {
    label: 'Average Mentions Per Day',
    tooltip: 'The estimated daily posts matching your search criteria during the reporting period',
    type: 'Total',
  },
  AVG_ENGAGEMENTS_PER_POST: {
    label: 'Average Engagements',
    tooltip:
      'The average number of engagements on posts matching your search criteria during the reporting period',
    type: 'Average',
  },
};

export const SOCIAL_LISTENING_TOPIC_TOP_LINE_STATS = {
  MENTIONS: {
    label: 'Mentions',
    tooltip:
      'The estimated number of published posts within this Topic during the reporting period',
    type: 'Total',
  },
  ENGAGEMENTS: {
    label: 'Engagements',
    tooltip:
      'The estimated number of engagements on posts within this Topic during the reporting period',
    type: 'Total',
  },
  AVG_ENGAGEMENTS_PER_POST: {
    label: 'Average Engagements',
    tooltip:
      'The estimated average number of engagements per post within this Topic during the reporting period',
    type: 'Average',
  },
  IMPRESSIONS: {
    label: 'Impressions',
    tooltip:
      'The estimated number of impressions on posts within this Topic during the reporting period. ' +
      'Impressions are estimated for Facebook, Instagram, X and Youtube.',
    type: 'Total',
  },
};

export const SENTIMENT_TREND_CHART_OPTIONS = Object.freeze({
  legendSort: {
    POSITIVE: 1,
    NEUTRAL: 2,
    NEGATIVE: 3,
  },
  sentimentColors: {
    POSITIVE: '#24a677',
    NEUTRAL: '#4299e9',
    NEGATIVE: '#f46565',
  },
});

export const SOCIAL_LISTENING_DRAWERS = {
  TOP_POST: 'socialListeningTopPerformingPost',
  TRENDING_KEYWORD: 'socialListeningTrendingKeyword',
  VISUAL_TRENDS: 'socialListeningVisualTrends',
  VISUAL_TREND: 'socialListeningVisualTrend',
  TOPIC_ALERT: 'socialListeningTopicAlert',
  VISUAL_FILTERS: 'socialListeningVisualFilters',
  MEDIA_POST: 'socialListeningMediaPost',
  TOP_WEB_RESULTS: 'socialListeningTopWebResults',
  WEB_RESULTS: 'socialListeningWebResults',
  MANAGE_TOPIC_LIMITS: 'socialListeningManageTopicLimits',
  TOPIC_TEMPLATE_LIST: 'socialListeningTopicTemplateList',
  TOPIC_TEMPLATE_CREATOR: 'socialListeningTopicTemplateCreator',
  TOPIC_PREVIEW: 'socialListeningTopicPreview',
};

export const TOP_POSTS_SORT_OPTIONS = [POST_SORT_OPTIONS.engagements, POST_SORT_OPTIONS.date];

export const TOP_POSTS_DEFAULT_SORT_OPTION = {
  sortOrder: 'DESC',
  selected: POST_SORT_OPTIONS.engagements,
};

export const WEB_RESULTS_DEFAULT_SORT_OPTION = {
  sortOrder: 'DESC',
  selected: POST_SORT_OPTIONS.date,
};
export const POST_DETAIL_ICON_MAP = {
  INSTAGRAM: {
    icon: 'instagram-mono',
  },
};

export const VISUAL_FILTER_MEDIA_SOURCES = Object.freeze({
  topPerforming: { title: 'Top Performing', value: 'topPerforming', order: 1 },
  trends: { title: 'Trends', value: 'trends', order: 2 },
  library: { title: 'Library', value: 'library', order: 3 },
  uploadMedia: { title: 'Upload Media', value: 'uploadMedia', order: 4 },
});

export const VISUAL_FILTER_MEDIA_SOURCE_TABS = sortBy(
  Object.values(VISUAL_FILTER_MEDIA_SOURCES),
  'order',
);

export const PLATFORM_STAT_CARD_MAPPINGS = {
  default: {
    totalPosts: 0,
    uniqueCreators: 0,
    totalEngagements: 0,
    averageEngagementsPerPost: 0,
    averageMentionsPerDay: 0,
  },
  mappings: {
    instagram: {
      totalPosts: 'totalPosts',
      uniqueCreators: 'uniqueCreators',
      totalEngagements: 'totalEngagements',
      averageEngagementsPerPost: 'averageEngagementsPerPost',
      averageMentionsPerDay: 'averageMentionsPerDay',
    },
    twitter: {
      totalPosts: 'total_count',
    },
    youtube: {
      totalPosts: 'total_count',
    },
    webz: {
      totalPosts: 'totalResults',
    },
  },
};

export const TOP_POSTS_STATE_KEY = {
  TRENDS_TOP_POSTS_PREVIEW: 'trendsTopPostsPreview',
  TRENDS_TOP_POSTS: 'trendsTopPosts',
  TOPICS_TOP_POSTS_PREVIEW: 'topicsTopPostsPreview',
  TOPICS_TOP_POSTS: 'topicsTopPosts',
  TOPIC_CREATOR_TOP_POSTS_PREVIEW: 'topicCreatorTopPostsPreview',
  TOPIC_CREATOR_TOP_POSTS: 'topicCreatorTopPosts',
  TRENDS_DRAWER_TOP_POSTS_PREVIEW: 'trendsDrawerTopPostsPreview',
  TRENDS_DRAWER_TOP_POSTS: 'trendsDrawerTopPosts',
  TOPICS_DRAWER_TOP_POSTS_PREVIEW: 'topicsDrawerTopPostsPreview',
  TOPICS_DRAWER_TOP_POSTS: 'topicsDrawerTopPosts',
  COMPARISON_TOP_POSTS: 'comparisonTopPosts',
  COMPARISON_DRAWER_TOP_POSTS: 'comparisonDrawerTopPosts',
};

export const DEMOGRAPHICS_CHART_COLOR_CONFIG = {
  GENDER: {
    backgroundColor: ['rgba(122,141,243,1)', 'rgba(247,92,156,1)', 'rgba(25,166,149,1)'],
    hoverBackgroundColor: ['rgba(122,141,243,0.7)', 'rgba(247,92,156,0.7)', 'rgba(25,166,149,0.7)'],
  },
};

export const TOPIC_TEMPLATE_BANNER = {
  TITLE: 'Start with a Template',
  DESCRIPTION:
    'Compare Share of Voice, track brand health, or explore Vision AI-suggested Topics with ready-to-use templates',
};

export const SOCIAL_LISTENING_IMPORT_EVENT_ORIGIN = {
  IG_HASHTAG_MONITORING_V2: 'IG_HASHTAG_MONITORING_V2',
};

export const TOPIC_TEMPLATE_CARDS = {
  BRAND_HEALTH: {
    title: 'Brand Health',
    description: 'Monitor public perception of your brand',
    icon: 'heart',
  },
  COMPETITIVE_ANALYSIS: {
    title: 'Competitive Analysis',
    description: 'Compare your Share of Voice against competitors',
    icon: 'horizontal-bar-chart',
  },
  VISION_AI_SUGGESTED: {
    title: 'Vision-AI Suggested Topics',
    description: 'Explore Topics based on your brand’s most recent content',
    icon: 'vision-ai',
  },
  SINGULAR_TOPIC: {
    title: 'Custom Topic',
    description: 'Start a blank Topic from scratch',
    icon: 'file',
  },
};

// TOPIC COMPARISONS
export const TOPIC_COMPARISONS_TOOLTIPS = {
  MENTIONS_TREND:
    'The estimated number of published posts for each Topic in this Comparison during the reporting period',
  MENTIONS_BY_CHANNEL:
    'The estimated number of published posts for each Topic in this Comparison by channel',
  TOP_PERFORMING_POSTS:
    'A sample of the most engaging posts for each Topic in this Comparison during the reporting period',
  MENTIONS_BY_GENDER:
    'The percentage of published posts for each Topic in this Comparison by Gender. ' +
    'Gender is estimated based on public profile data when it is available. ' +
    "Accounts that don't specify a gender, such as businesses, pets, or accounts shared by multiple people, " +
    'are classified as Other.',
  MENTIONS_BY_AGE:
    'The percentage of published posts for each Topic in this Comparison by Age. Age is estimated based on public profile data when it is available.',
  MENTIONS:
    'The estimated number of published posts for each Topic in this Comparison during the reporting period',
  SHARE_OF_VOICE: {
    content:
      "The percentage breakdown for each Topic's mentions compared to the total mentions across all Topics in this Comparison during the reporting period.",
    footer:
      'Share of Voice = (Topic Mentions / Total Mentions across all Topics in this Comparison) * 100',
  },
  ENGAGEMENTS:
    'The estimated number of engagements on posts for each Topic in this Comparison during the reporting period.',
  IMPRESSIONS:
    'The estimated number of impressions on posts for each Topic in this Comparison during the reporting period. Impressions are estimated for Facebook, Instagram, X and Youtube.',
  NET_SENTIMENT: {
    content:
      'The net sentiment of post captions within each Topic in this Comparison during the reporting period. Net sentiment shows the overall balance between positive and negative conversation.',
    footer: 'Net Sentiment = (Positive Captions - Negative Captions) / Total Captions * 100',
  },
  MENTIONS_BY_LOCATION:
    'The most frequent author locations for each Topic in this Comparison during the reporting period. Location is estimated based on public profile data when it is available.',
  TOP_KEYWORDS:
    'The top keywords in post captions for each Topic in this Comparison during the reporting period',
  CREATE_TOPIC_BUTTON_DISABLED: 'Topic limit reached',
};

export const COMPARISON_TOPIC_EXISTS_BANNER =
  'A Topic with this name already exists. You can replace this Topic with the existing one or rename the Topic.';

export const TOPIC_COMPARISON_MAX_TOPIC_COUNT = 5;

export const TOPIC_TEMPLATE_FLOWS = {
  BRAND_HEALTH: 'brandHealth',
  COMPETITIVE_ANALYSIS: 'competitiveAnalysis',
  VISION_AI_SUGGESTED: 'visionAiSuggested',
  SINGULAR_TOPIC: 'singularTopic',
  MULTIPLE_TOPIC: 'multipleTopic',
};

export const SOCIAL_LISTENING_COMPARISON_CREATION_FLOW = {
  CREATE_COMPARISON: 'Create Comparison',
  COMPETITIVE_ANALYSIS: 'Competitive Analysis',
};

export const POSTS_VIEW = {
  TILE: 'grid',
  LIST: 'table',
};
